import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetEmployeesListQuery } from '@app/api/Users';
import { EDIT_EMPLOYEE_PATH, SYSTEM_USER_ID } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { COPYING_SERVICES_FROM_EMPLOYEE_PARAM } from '@app/pages/Employees/EditEmployee/data/copyingServices';

import type { DefaultOptionType } from 'antd/es/select';

interface UseCopyEmployeeServicesModalParams {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  employeeId: number;
}

export const useCopyEmployeeServicesModal = ({
  isOpen,
  setIsOpen,
  employeeId,
}: UseCopyEmployeeServicesModalParams) => {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(
    null
  );

  const { params } = useUserPrint();
  const navigate = useNavigate();

  const {
    data: employeesData,
    error: employeesError,
    isFetching: isEmployeesFetching,
  } = useGetEmployeesListQuery(
    { ActiveBranchId: params.ActiveBranchId },
    { skip: !isOpen, refetchOnMountOrArgChange: true }
  );

  // Исполнители филиала, сгруппированные по должностям
  const employeesOptions = useMemo<DefaultOptionType[]>(() => {
    if (!employeesData) return [];

    const groups: Record<
      string,
      { label: string; options: DefaultOptionType[] }
    > = {};

    employeesData
      .filter(
        ({ Id, IsDeleted }) =>
          !IsDeleted && Id !== employeeId && Id !== SYSTEM_USER_ID
      )
      .forEach(
        ({ Id, NameFull, NameFirst, NameLast, PositionId, Position }) => {
          const groupKey =
            PositionId !== null ? `position-${PositionId}` : 'no-position';

          if (!groups[groupKey]) {
            groups[groupKey] = {
              label: Position || 'Без должности',
              options: [],
            };
          }

          const name =
            NameFull || `${NameLast || ''} ${NameFirst || ''}`.trim();

          groups[groupKey].options.push({
            key: `employee-${Id}-group-${groupKey}`,
            label: name,
            value: Id,
            filterName: name.toLowerCase(),
          });
        }
      );

    return Object.values(groups);
  }, [employeesData, employeeId]);

  const closeModalHandler = useCallback(() => {
    setIsOpen(false);
    setSelectedEmployeeId(null);
  }, [setIsOpen]);

  const navigateToEmployeeHandler = useCallback(() => {
    navigate(
      `${EDIT_EMPLOYEE_PATH}?id=${selectedEmployeeId}&key=5&${COPYING_SERVICES_FROM_EMPLOYEE_PARAM}=${employeeId}`
    );
    closeModalHandler();
  }, [closeModalHandler, employeeId, navigate, selectedEmployeeId]);

  return {
    selectedEmployeeId,
    setSelectedEmployeeId,
    employeesOptions,
    employeesError,
    isEmployeesFetching,
    closeModalHandler,
    navigateToEmployeeHandler,
  };
};
